import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, Mail, Plus, Save, ShieldCheck, Trash2, Users } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Avatar, Btn, Chip, Confirm, Empty, Field, FilePickButton, IconBtn, Loading, MediaViewer, Page, Section, Select, cx } from "../components/wf";
import { api, EMPLOYEE_STATUSES, fileUrl, money, ROLE_LABELS, uploadFile } from "../lib/api";
import { usePerms } from "../lib/auth";
import { useEmployees, useKastle } from "../lib/queries";
import { ExpiringDocsBanner, KastleJobs, ShareDocDialog } from "../components/TeamExtras";
import { DraftBanner, SaveStatus, useAutoSave, useDraft } from "../lib/autosave";

const empBody = (f) => ({ name: f.name, phone: f.phone, email: f.email, hourly_rate: parseFloat(f.hourly_rate) || 0, role: f.role, status: f.status, photo_path: f.photo_path, documents: f.documents });
import { todayISO } from "../lib/api";

export function Team() {
  const emps = useEmployees();
  const kastle = useKastle();
  const nav = useNavigate();
  const { isManager, seeMoney } = usePerms();
  const [tab, setTab] = useState("employees");
  return (
    <Page title="Team" subtitle="Employees & Kastle project managers" wide testid="team-page"
      right={isManager && <Btn icon={Plus} data-testid="add-team-button" onClick={() => nav(tab === "employees" ? "/employees/new" : "/kastle/new")}>{tab === "employees" ? "New Employee" : "New Kastle PM"}</Btn>}>
      <div className="mb-5 inline-flex rounded-xl bg-wf-s3 p-1">
        {[["employees", `Employees (${emps.data?.length ?? 0})`], ["kastle", `Kastle PMs (${kastle.data?.length ?? 0})`]].map(([k, l]) => (
          <button key={k} data-testid={`team-tab-${k}`} onClick={() => setTab(k)} className={cx("h-9 rounded-lg px-5 text-sm font-semibold", tab === k ? "bg-wf-surface shadow-sm" : "text-wf-mute")}>{l}</button>
        ))}
      </div>
      {tab === "employees" ? (
        emps.isLoading ? <Loading /> : !emps.data?.length ? <Empty icon={Users} title="No employees yet" /> : (
          <>
          {isManager && <ExpiringDocsBanner employees={emps.data} />}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="employees-list">
            {emps.data.map((e) => (
              <Link key={e.id} to={`/employees/${e.id}`} data-testid={`employee-row-${e.id}`} className={cx("flex items-center gap-3 rounded-2xl border border-wf-line p-4 hover:bg-wf-s2", (e.status ?? "active") !== "active" && "opacity-60")}>
                <Avatar name={e.name} src={e.photo_path} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold">{e.name}</p>
                  <p className="truncate text-sm text-wf-mute">{[e.role, e.login ? ROLE_LABELS[e.login.role] + " login" : null, (e.status ?? "active") !== "active" && EMPLOYEE_STATUSES.find((s) => s.value === e.status)?.label].filter(Boolean).join(" · ")}</p>
                </div>
                {seeMoney && <span className="text-sm font-bold text-wf-brand">{money(e.hourly_rate)}/hr</span>}
              </Link>
            ))}
          </div>
          </>
        )
      ) : kastle.isLoading ? <Loading /> : !kastle.data?.length ? <Empty icon={ShieldCheck} title="No Kastle PMs yet" /> : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="kastle-list">
          {kastle.data.map((k) => (
            <Link key={k.id} to={`/kastle/${k.id}`} data-testid={`kastle-row-${k.id}`} className="flex items-center gap-3 rounded-2xl border border-wf-line p-4 hover:bg-wf-s2">
              <Avatar name={k.name} />
              <div className="min-w-0"><p className="truncate font-bold">{k.name}</p><p className="truncate text-sm text-wf-mute">{[k.location, k.phone, k.email].filter(Boolean).join(" · ")}</p></div>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

function EmployeeLogin({ emp, onChange }) {
  const [email, setEmail] = useState(emp.login?.email ?? emp.email ?? "");
  const [role, setRole] = useState(emp.login?.role ?? "technician");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await api(`/employees/${emp.id}/login`, { method: "PUT", body: { email: email.trim(), role, send_invite: invite, ...(password ? { password } : {}) } });
      setPassword("");
      onChange();
      toast.success(res.invite ? (res.invite.ok ? `Invite sent to ${res.invite.to}` : `Login saved, invite not sent: ${res.invite.error}`) : "Login saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api(`/employees/${emp.id}/login`, { method: "DELETE" });
      onChange();
      toast.success("Login removed");
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div data-testid="employee-login-section">
      <Section title="App Login" />
      {emp.login && (emp.status ?? "active") !== "active" && (
        <p data-testid="employee-login-disabled" className="mb-2 text-sm font-semibold text-wf-warn">Login is turned off while this employee isn't Active.</p>
      )}
      <p className="mb-3 text-sm text-wf-mute">{emp.login ? `${emp.login.email} · ${ROLE_LABELS[emp.login.role]}${emp.login.disabled ? " · turned off" : ""}` : "No login yet — add one so they can sign in."}</p>
      <Field label="Login Email" type="email" testid="employee-login-email" value={email} onChange={setEmail} />
      <Select label="Role" testid="employee-login-role" value={role} options={Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }))} onChange={setRole} />
      <Field label={emp.login ? "New Password (optional)" : "Password"} type="password" testid="employee-login-password" value={password} onChange={setPassword} />
      <div className="mb-4 flex gap-2">
        <Chip label="Don't email" selected={!invite} onClick={() => setInvite(false)} testid="employee-login-invite-off" />
        <Chip label="Email set-password link" selected={invite} onClick={() => setInvite(true)} testid="employee-login-invite-on" />
      </div>
      <div className="flex gap-2">
        <Btn variant="secondary" loading={saving} onClick={save} disabled={!email.trim()} data-testid="employee-login-save">{emp.login ? "Update Login" : "Create Login"}</Btn>
        {emp.login && <Btn variant="ghost" icon={Trash2} onClick={() => setConfirm(true)} data-testid="employee-login-remove">Remove Login</Btn>}
      </div>
      <Confirm open={confirm} title="Remove this login?" message="They won't be able to sign in anymore." onConfirm={remove} onClose={() => setConfirm(false)} confirmLabel="Remove" />
    </div>
  );
}

const emptyEmp = { name: "", phone: "", email: "", hourly_rate: "", role: "", status: "active", photo_path: null, documents: [] };

export function EmployeeForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isManager, isAdmin, seeMoney } = usePerms();
  const existing = useQuery({ queryKey: ["employee", id], queryFn: () => api(`/employees/${id}`), enabled: !!id });
  const [f, setF] = useState(emptyEmp);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [shareDoc, setShareDoc] = useState(null);
  const savedDocIds = (existing.data?.documents ?? []).map((d) => d.id);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const e = existing.data;
    if (e && !loaded) {
      setF({ ...emptyEmp, ...e, hourly_rate: e.hourly_rate ? String(e.hourly_rate) : "", status: e.status ?? "active", documents: e.documents ?? [] });
      setLoaded(true);
    }
  }, [existing.data, loaded]);
  const auto = useAutoSave(f, async (d) => {
    const res = await api(`/employees/${id}`, { method: "PUT", body: empBody(d) });
    qc.setQueryData(["employee", id], res);
    qc.invalidateQueries({ queryKey: ["employees"] });
  }, { ready: !!id && loaded, enabled: !!f.name.trim() });
  const draft = useDraft("draft:employee:new", f, setF, !id);

  const upload = async (files, kind) => {
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) paths.push(await uploadFile(file));
      if (kind === "photo") setF((s) => ({ ...s, photo_path: paths[0] }));
      else setF((s) => ({ ...s, documents: [...s.documents, ...paths.map((p) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, path: p, description: "" }))] }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter the employee name");
    setSaving(true);
    try {
      const res = await api(id ? `/employees/${id}` : "/employees", { method: id ? "PUT" : "POST", body: empBody(f) });
      if (!id) draft.clear();
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.setQueryData(["employee", res.id], res);
      toast.success(id ? "Employee updated" : "Employee added");
      if (!id) nav(`/employees/${res.id}`, { replace: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api(`/employees/${id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["employees"] });
      nav("/team");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (id && existing.isLoading) return <Loading />;
  return (
    <Page title={id ? f.name || "Employee" : "New Employee"} back testid="employee-form"
      right={<>
        {id && isManager && <IconBtn icon={Trash2} tone="danger" onClick={() => setConfirm(true)} data-testid="employee-delete-button" />}
        {id ? <SaveStatus status={auto.status} /> : <Btn icon={Save} loading={saving} onClick={save} data-testid="employee-save-button">Save</Btn>}
      </>}>
      <DraftBanner draft={draft} onDiscard={() => { draft.clear(); setF(emptyEmp); }} />
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <div className="mb-5 flex items-center gap-4">
            <Avatar name={f.name} src={f.photo_path} size={72} />
            <FilePickButton testid="employee-photo-button" variant="ghost" loading={uploading} onFiles={(fl) => upload(fl, "photo")}>{f.photo_path ? "Change photo" : "Add photo"}</FilePickButton>
          </div>
          <Field label="Employee Name *" testid="employee-name-input" value={f.name} onChange={set("name")} />
          <Field label="Phone Number" testid="employee-phone-input" value={f.phone} onChange={set("phone")} />
          <Field label="Email Address" type="email" testid="employee-email-input" value={f.email} onChange={set("email")} />
          {seeMoney && <Field label="Hourly Rate ($)" type="number" step="0.01" testid="employee-rate-input" value={f.hourly_rate} onChange={set("hourly_rate")} placeholder="0.00" />}
          <Field label="Role" testid="employee-role-input" value={f.role} onChange={set("role")} placeholder="Technician" />
          {isManager && <Select label="Status" testid="employee-status-select" value={f.status} options={EMPLOYEE_STATUSES} onChange={set("status")} />}
        </div>
        <div>
          <Section title={`Documents (${f.documents.length})`}>
            <FilePickButton testid="add-document-button" variant="ghost" icon={FileUp} accept="image/*,application/pdf" multiple loading={uploading} onFiles={(fl) => upload(fl, "doc")}>Add</FilePickButton>
          </Section>
          {f.documents.map((d, i) => (
            <div key={d.id} className="mb-2 flex flex-wrap items-center gap-3 rounded-xl border border-wf-line p-3" data-testid={`employee-doc-${i}`}>
              <button onClick={() => setViewer(d.path)}><img src={fileUrl(d.path)} alt="" className="h-14 w-14 rounded-lg bg-wf-s3 object-cover" /></button>
              <input value={d.description} placeholder="Description (license, certificate…)" data-testid={`employee-doc-desc-${i}`}
                onChange={(e) => setF((s) => ({ ...s, documents: s.documents.map((x) => (x.id === d.id ? { ...x, description: e.target.value } : x)) }))}
                className="h-10 min-w-[160px] flex-1 rounded-lg border border-wf-line bg-wf-s2 px-3 text-sm outline-none focus:border-wf-brand" />
              {savedDocIds.includes(d.id) && <IconBtn icon={Mail} onClick={() => setShareDoc({ employeeId: id, docId: d.id, title: d.description })} data-testid={`document-share-${i}`} title="Share by email" />}
              <IconBtn icon={Trash2} tone="danger" onClick={() => setF((s) => ({ ...s, documents: s.documents.filter((x) => x.id !== d.id) }))} data-testid={`employee-doc-remove-${i}`} />
              <label className="flex w-full items-center gap-2 pl-[68px] text-xs font-semibold text-wf-mute">
                Expires
                <input type="date" data-testid={`employee-doc-expires-${i}`} value={d.expires_on ?? ""}
                  onChange={(e) => setF((s) => ({ ...s, documents: s.documents.map((x) => (x.id === d.id ? { ...x, expires_on: e.target.value } : x)) }))}
                  className="h-8 rounded-lg border border-wf-line bg-wf-s2 px-2 text-sm text-wf-ink outline-none focus:border-wf-brand" />
                {!d.expires_on && <span>No expiration date</span>}
                {!!d.expires_on && d.expires_on < todayISO() && <span className="font-bold text-wf-err" data-testid={`employee-doc-expired-${i}`}>Expired</span>}
                {!!d.expires_on && <button type="button" onClick={() => setF((s) => ({ ...s, documents: s.documents.map((x) => (x.id === d.id ? { ...x, expires_on: "" } : x)) }))} className="text-wf-brand hover:underline" data-testid={`employee-doc-expires-clear-${i}`}>Clear</button>}
              </label>
            </div>
          ))}
          {!f.documents.length && <p className="text-sm text-wf-mute">No documents yet.</p>}
          {id && isAdmin && existing.data && <EmployeeLogin emp={existing.data} onChange={() => { existing.refetch(); qc.invalidateQueries({ queryKey: ["employees"] }); }} />}
        </div>
      </div>
      <MediaViewer path={viewer} onClose={() => setViewer(null)} />
      <ShareDocDialog target={shareDoc} onClose={() => setShareDoc(null)} />
      <Confirm open={confirm} title="Delete this employee?" onConfirm={remove} onClose={() => setConfirm(false)} />
    </Page>
  );
}

export function KastleForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isManager } = usePerms();
  const existing = useQuery({ queryKey: ["kastle", id], queryFn: () => api(`/kastle/${id}`), enabled: !!id });
  const [f, setF] = useState({ name: "", phone: "", email: "", location: "" });
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    const k = existing.data;
    if (k && !loaded) {
      setF({ name: k.name, phone: k.phone, email: k.email, location: k.location });
      setLoaded(true);
    }
  }, [existing.data, loaded]);
  const kAuto = useAutoSave(f, async (d) => {
    await api(`/kastle/${id}`, { method: "PUT", body: d });
    qc.invalidateQueries({ queryKey: ["kastle"] });
  }, { ready: !!id && loaded, enabled: !!f.name.trim() });
  const kDraft = useDraft("draft:kastle:new", f, setF, !id);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter the project manager's name");
    setSaving(true);
    try {
      await api(id ? `/kastle/${id}` : "/kastle", { method: id ? "PUT" : "POST", body: f });
      if (!id) kDraft.clear();
      qc.invalidateQueries({ queryKey: ["kastle"] });
      toast.success(id ? "Kastle PM updated" : "Kastle PM added");
      nav(-1);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api(`/kastle/${id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["kastle"] });
      nav("/team");
    } catch (e) {
      toast.error(e.message);
    }
  };
  if (id && existing.isLoading) return <Loading />;
  return (
    <Page title={id ? "Kastle PM" : "New Kastle PM"} back testid="kastle-form"
      right={<>
        {id && isManager && <IconBtn icon={Trash2} tone="danger" onClick={() => setConfirm(true)} data-testid="kastle-delete-button" />}
        {id ? <SaveStatus status={kAuto.status} /> : <Btn icon={Save} loading={saving} onClick={save} data-testid="kastle-save-button">Save</Btn>}
      </>}>
      <DraftBanner draft={kDraft} onDiscard={() => { kDraft.clear(); setF({ name: "", phone: "", email: "", location: "" }); }} />
      <div className="max-w-xl">
        <Field label="Project Manager's Name *" testid="kastle-name-input" value={f.name} onChange={set("name")} />
        <Field label="Phone Number" testid="kastle-phone-input" value={f.phone} onChange={set("phone")} />
        <Field label="Email Address" type="email" testid="kastle-email-input" value={f.email} onChange={set("email")} />
        <Field label="Location" testid="kastle-location-input" value={f.location} onChange={set("location")} />
      </div>
      {!!id && isManager && <div className="max-w-xl"><KastleJobs pmId={id} seeMoney={isManager} /></div>}
      <Confirm open={confirm} title="Delete this Kastle PM?" onConfirm={remove} onClose={() => setConfirm(false)} />
    </Page>
  );
}
